/**
 * @Author: LiuChang
 * @Date: 2026-06-04 15:38:32
 * @LastEditTime: 2026-06-09 10:34:07
 * @LastEditors: LiuChang
 * @Description: 阿里云oss直传工具
 * @FilePath: \xianyouhezz-web\utils\oss-upload.js
 * @可以输入预定的版权声明、个性签名、空行等
 */
import config from '@/utils/config';
import { Encrypt, Decrypt } from '@/utils/aes';
import { fetchAliyunOssToken, fetchAliyunOssParams } from '@/api/upload.js';

// ==================== 常量配置 ====================

/** OSS 存储空间地址 */
const OSS_BUCKET_HOST = config.aliyunImageUrl;

/**
 * 图片类型配置
 * MIME 类型映射表
 */
const IMAGE_MIME_TYPES = {
	// JPEG
	'image/jpeg': ['.jpg', '.jpeg', '.jpe', '.jfif'],
	// PNG
	'image/png': ['.png'],
	// WebP
	'image/webp': ['.webp'],
	// GIF
	'image/gif': ['.gif'],
	// BMP
	'image/bmp': ['.bmp']
	// // SVG
	// "image/svg+xml": [".svg"],
	// // TIFF
	// "image/tiff": [".tiff", ".tif"],
	// // ICO
	// "image/x-icon": [".ico"],
	// // HEIC/HEIF
	// "image/heic": [".heic"],
	// "image/heif": [".heif"],
	// // AVIF
	// "image/avif": [".avif"]
};

/**
 * 视频类型配置
 * MIME 类型映射表
 */
const VIDEO_MIME_TYPES = {
	// MP4
	'video/mp4': ['.mp4', '.m4v'],
	// WebM
	'video/webm': ['.webm'],
	// MOV（QuickTime 格式，iPhone 录制的视频常用）
	'video/quicktime': ['.mov', '.qt']
	// // OGG
	// "video/ogg": [".ogg", ".ogv"],
	// // AVI
	// "video/x-msvideo": [".avi"],
	// // FLV
	// "video/x-flv": [".flv"],
	// // WMV
	// "video/x-ms-wmv": [".wmv"],
	// // MKV
	// "video/x-matroska": [".mkv"],
	// // 3GP
	// "video/3gpp": [".3gp", ".3gpp"],
	// // TS
	// "video/mp2t": [".ts"],
	// // M4V
	// "video/x-m4v": [".m4v"]
};

/** 所有允许的 MIME 类型（图片 + 视频） */
const ALLOWED_MIME_TYPES = [...Object.keys(IMAGE_MIME_TYPES), ...Object.keys(VIDEO_MIME_TYPES)];

/** 图片最大文件大小：10MB */
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;

/** 视频最大文件大小：100MB */
const MAX_VIDEO_SIZE_BYTES = 100 * 1024 * 1024;

// ==================== 内部状态 ====================

/** 是否正在上传中（用于防止重复提交） */
let isFileUploading = false;

/** 正在请求中的 OSS Token Promise（用于并发请求复用） */
let pendingTokenPromise = null;

/** 缓存的 OSS Token 数据 */
let cachedTokenData = null;

/** Token 过期时间戳（毫秒） */
let tokenExpireTimestamp = 0;

/** 正在请求中的签名参数 Promise（用于并发请求复用） */
let pendingSignaturePromise = null;

/** 缓存的签名参数公共部分（不含 key） */
let cachedCommonParams = null;

/** 缓存的 dir 路径 */
let cachedUploadDir = null;

/** 签名参数过期时间戳（毫秒） */
let signatureExpireTimestamp = 0;

// ==================== 工具函数 ====================

/**
 * 生成唯一的上传文件id
 * 格式：13位时间戳 + 用户ID补位 + 随机数
 *
 * @param {string|number} userId - 用户ID
 * @returns {string} 文件id
 */
function generateUploadFileId(userId = '') {
	const userIdStr = typeof userId === 'string' ? userId : String(userId);
	const currentTimestamp = Date.now();
	const randomDigitsCount = 11 - userIdStr.length;
	const randomNumber = Math.floor(Math.random() * Math.pow(10, randomDigitsCount))
		.toString()
		.padStart(randomDigitsCount, '0');

	return `${currentTimestamp}${userIdStr}${randomNumber}`;
}

/**
 * 判断文件是否为图片类型
 *
 * @param {File} file - 文件对象
 * @returns {boolean}
 */
function isImageFile(file) {
	return file.type in IMAGE_MIME_TYPES;
}

/**
 * 判断文件是否为视频类型
 *
 * @param {File} file - 文件对象
 * @returns {boolean}
 */
function isVideoFile(file) {
	return file.type in VIDEO_MIME_TYPES;
}

/**
 * 获取文件类型的中文描述
 *
 * @param {File} file - 文件对象
 * @returns {string} "图片" | "视频" | "文件"
 */
function getFileTypeLabel(file) {
	if (isImageFile(file)) return '图片';
	if (isVideoFile(file)) return '视频';
	return '文件';
}

/**
 * 请求 OSS 上传签名参数
 *
 * 缓存策略说明：
 * - OSS Token：按 expire 缓存，所有文件共享
 * - 签名公共参数（policy、signature 等）：按 expire 缓存，只有 key 不同
 * - key 结构：{dir}/{随机数}.{后缀}，每次用新随机数 + 文件后缀组装
 *
 * 并发上传多文件时：
 * - Token 只会请求一次接口
 * - 签名公共参数只会请求一次接口
 * - 后续文件直接复用缓存，只替换 key
 *
 * @param {Object} params - 请求参数
 * @param {File} params.file - 要上传的文件对象
 * @param {string|number} params.userId - 用户ID
 * @returns {Promise<Object>} 签名数据对象，包含 OSSAccessKeyId、policy、signature、key 等
 */
async function requestOSSUploadSignature({
	file,
	userId
}) {
	const currentTimestamp = Date.now();

	// 第一步：获取 OSS Token
	// Token 是通用的身份凭证，可以缓存给多个文件使用
	let ossSecurityToken;
	if (cachedTokenData && currentTimestamp < tokenExpireTimestamp) {
		// 缓存有效，直接使用
		ossSecurityToken = cachedTokenData;
	} else if (pendingTokenPromise) {
		// 正在请求中，复用同一个 Promise（避免并发重复请求）
		ossSecurityToken = await pendingTokenPromise;
	} else {
		// 缓存过期且没有进行中的请求，发起新请求
		pendingTokenPromise = (async () => {
			try {
				const tokenResponse = await fetchAliyunOssToken();
				const tokenData = JSON.parse(Decrypt(tokenResponse.data)) || {};
				const token = tokenData.multipart.aliosstoken;

				// 缓存 Token
				cachedTokenData = token;
				// 设置过期时间（秒转毫秒）
				if (tokenData.multipart.expire) {
					tokenExpireTimestamp = tokenData.multipart.expire * 1000;
				} else {
					// 兜底：默认 30 分钟后过期
					tokenExpireTimestamp = currentTimestamp + 30 * 60 * 1000;
				}
				return token;
			} finally {
				pendingTokenPromise = null;
			}
		})();
		ossSecurityToken = await pendingTokenPromise;
	}
    

	// 第二步：获取签名公共参数

	// 生成唯一的文件id
	const fileId = generateUploadFileId(userId);

	// 获取当前文件的后缀名（包含 .）
	const fileExtension = file.name.substring(file.name.lastIndexOf('.'));

	/**
	 * 从缓存的公共参数和 dir 组装完整的签名数据
	 * @param {Object} commonParams - 缓存的公共参数（不含 key）
	 * @returns {Object} 完整的签名数据
	 */
	const buildSignatureData = (commonParams) => {
		// 使用缓存的 dir + 新的随机数 + 当前文件后缀，组装新的 key
		// 例如：uploads/20260605/ + 178062159018312395777120 + .png
		const fileKey = `${cachedUploadDir}${fileId}${fileExtension}`;
		return {
			...commonParams,
			aliosstoken: ossSecurityToken,
			key: fileKey,
			host: OSS_BUCKET_HOST
		};
	};

	// 情况1：公共参数缓存有效，直接复用
	if (cachedCommonParams && currentTimestamp < signatureExpireTimestamp) {
		return buildSignatureData(cachedCommonParams);
	}

	// 情况2：正在请求中，复用同一个 Promise
	if (pendingSignaturePromise) {
		const commonParams = await pendingSignaturePromise;
		return buildSignatureData(commonParams);
	}

	// 情况3：缓存过期且没有进行中的请求，发起新请求
	pendingSignaturePromise = (async () => {
		try {
			const signatureResponse = await fetchAliyunOssParams({
				aliosstoken: ossSecurityToken,
				method: 'POST',
				category: '',
				md5: fileId,
				name: file.name,
				type: file.type,
				size: file.size
			});

			const responseData = signatureResponse.data;

			// 从原始 key 中提取公共部分
			// key 格式示例：uploads/20260605/178062159018312395777120.png
			// lastIndexOf("/") 定位最后一个 / 的位置
			// dir：uploads/20260605/（最后一个 / 之前的内容 + /）
			// 后缀：.png（最后一个 . 之后的内容，上传时动态拼装）
			const originalKey = responseData.key;
			const lastSlashIndex = originalKey.lastIndexOf('/');

			// 缓存 dir 路径（包含末尾的 /）
			cachedUploadDir = originalKey.substring(0, lastSlashIndex + 1);

			// 提取公共参数（排除 key，key 每个文件都不同，需要动态拼装）
			const { key, ...commonParams } = responseData;
			cachedCommonParams = commonParams;

			// 设置签名参数过期时间（秒转毫秒）
			if (responseData.expire) {
				signatureExpireTimestamp = responseData.expire * 1000;
			} else {
				signatureExpireTimestamp = currentTimestamp + 30 * 60 * 1000;
			}

			return commonParams;
		} finally {
			// 无论成功或失败，都要清除 pending 状态，避免死锁
			pendingSignaturePromise = null;
		}
	})();

	const commonParams = await pendingSignaturePromise;
	return buildSignatureData(commonParams);
}

/**
 * 使用 FormData 方式直传文件到 OSS
 *
 * @param {File} file - 文件对象
 * @param {Object} signatureData - OSS 签名数据
 * @param {Function} [onProgressCallback] - 上传进度回调函数，接收百分比数字
 * @returns {Promise<{code: number, fileUrl: string}>} 上传结果
 */
function uploadFormDataToOSS(file, signatureData, onProgressCallback) {
	return new Promise((resolve, reject) => {
		const formPayload = new FormData();

		// 按照 OSS 签名验证的重要顺序添加表单字段（file 必须放在最后）
		formPayload.append('OSSAccessKeyId', signatureData.OSSAccessKeyId); // 1. 身份认证：访问密钥 ID
		formPayload.append('aliosstoken', signatureData.aliosstoken); // 2. 临时授权：STS 安全令牌
		formPayload.append('policy', signatureData.policy); // 3. 权限控制：上传策略
		formPayload.append('signature', signatureData.signature); // 4. 安全验证：签名信息
		formPayload.append('key', signatureData.key); // 5. 资源定位：文件存储路径
		formPayload.append('expire', signatureData.expire); // 6. 时效控制：签名过期时间
		formPayload.append('callback', signatureData.callback); // 7. 回调通知：上传完成回调
		formPayload.append('success_action_status', signatureData.success_action_status); // 8. 响应状态：成功状态码
		formPayload.append('id', signatureData.id); // 9. 业务标识：上传任务 ID
		// file 必须作为最后一个表单域
		formPayload.append('file', file); // 10. 文件数据：实际文件内容

		const xmlHttpRequest = new XMLHttpRequest();

		// 监听上传进度事件
		if (typeof onProgressCallback === 'function') {
			xmlHttpRequest.upload.addEventListener('progress', (progressEvent) => {
				if (progressEvent.lengthComputable) {
					const uploadPercentage = Math.round((progressEvent.loaded / progressEvent.total) * 100);
					onProgressCallback(uploadPercentage);
				}
			});
		}

		xmlHttpRequest.open('POST', signatureData.host);

		xmlHttpRequest.onload = () => {
			if (xmlHttpRequest.status === 200) {
				const uploadedFileUrl = `${config.imageUrl}/${signatureData.key}`;
				resolve({
					code: 200,
					fileUrl: uploadedFileUrl
				});
			} else {
				reject(new Error(`上传失败，服务器响应状态码: ${xmlHttpRequest.status}`));
			}
		};

		xmlHttpRequest.onerror = () => reject(new Error('网络连接失败，请检查网络后重试'));
		xmlHttpRequest.ontimeout = () => reject(new Error('上传超时，请重试'));

		xmlHttpRequest.send(formPayload);
	});
}

// ==================== 公开 API ====================

/**
 * 验证媒体文件（图片或视频）的类型和大小
 *
 * @param {File} file - 文件对象
 * @param {Object} [options] - 验证配置选项
 * @param {Function} [options.onError] - 验证失败回调，接收错误消息字符串
 * @param {string[]} [options.allowedTypes] - 自定义允许的文件类型，不传则使用默认全部类型
 * @param {number} [options.maxImageSize] - 自定义图片最大大小（MB），不传则使用默认 10MB
 * @param {number} [options.maxVideoSize] - 自定义视频最大大小（MB），不传则使用默认 100MB
 * @returns {boolean} 验证是否通过
 *
 * @example
 * // 使用默认配置
 * const isValid = validateMediaFile(file, {
 *   onError: (errorMsg) => this.$message.error(errorMsg)
 * })
 *
 * @example
 * // 只允许图片，且限制 5MB
 * const isValid = validateMediaFile(file, {
 *   allowedTypes: ["image/jpeg", "image/png", "image/webp"],
 *   maxImageSize: 5,
 *   onError: (errorMsg) => this.$message.error(errorMsg)
 * })
 *
 * @example
 * // 只允许视频，且限制 200MB
 * const isValid = validateMediaFile(file, {
 *   allowedTypes: ["video/mp4", "video/webm"],
 *   maxVideoSize: 200,
 *   onError: (errorMsg) => this.$message.error(errorMsg)
 * })
 */
export function validateMediaFile(file, options = {}) {
	const { onError, allowedTypes, maxImageSize, maxVideoSize } = options;

	// 确定实际使用的允许类型列表
	const effectiveAllowedTypes = allowedTypes || ALLOWED_MIME_TYPES;

	// 确定实际的大小限制（MB 转 Bytes）
	const effectiveMaxImageSizeBytes = maxImageSize ? maxImageSize * 1024 * 1024 : MAX_IMAGE_SIZE_BYTES;

	const effectiveMaxVideoSizeBytes = maxVideoSize ? maxVideoSize * 1024 * 1024 : MAX_VIDEO_SIZE_BYTES;

	// 校验文件类型
	const isTypeAllowed = effectiveAllowedTypes.includes(file.type);

	if (!isTypeAllowed) {
		// 根据限制类型给出更精确的错误提示
		let errorMessage = '不支持的文件格式';

		if (allowedTypes) {
			// 用户自定义了类型限制
			const hasImageTypes = allowedTypes.some((type) => type.startsWith('image/'));
			const hasVideoTypes = allowedTypes.some((type) => type.startsWith('video/'));

			if (hasImageTypes && hasVideoTypes) {
				errorMessage = '不支持的文件格式，请上传指定的图片或视频文件';
			} else if (hasImageTypes) {
				errorMessage = '不支持的文件格式，请上传指定的图片文件';
			} else if (hasVideoTypes) {
				errorMessage = '不支持的文件格式，请上传指定的视频文件';
			}
		} else {
			errorMessage = '不支持的文件格式，请上传常见图片或视频文件';
		}

		if (typeof onError === 'function') {
			onError(errorMessage);
		}
		return false;
	}

	// 校验文件大小
	const fileSizeBytes = file.size;
	const fileTypeLabel = getFileTypeLabel(file);

	if (isImageFile(file) && fileSizeBytes > effectiveMaxImageSizeBytes) {
		const maxSizeMB = effectiveMaxImageSizeBytes / (1024 * 1024);
		const errorMessage = `${fileTypeLabel}大小不能超过 ${maxSizeMB}MB`;
		if (typeof onError === 'function') {
			onError(errorMessage);
		}
		return false;
	}

	if (isVideoFile(file) && fileSizeBytes > effectiveMaxVideoSizeBytes) {
		const maxSizeMB = effectiveMaxVideoSizeBytes / (1024 * 1024);
		const errorMessage = `${fileTypeLabel}大小不能超过 ${maxSizeMB}MB`;
		if (typeof onError === 'function') {
			onError(errorMessage);
		}
		return false;
	}

	return true;
}

/**
 * 上传文件到阿里云 OSS（完整流程）
 * 包含：请求签名 → 直传文件
 *
 * @param {Object} uploadOptions - 上传配置项
 * @param {File} uploadOptions.file - 要上传的文件对象
 * @param {string|number} uploadOptions.userId - 当前用户ID
 * @param {Object} uploadOptions.uploadApi - 获取签名参数的 API 实例（this.$uploadApi）
 * @param {Object} uploadOptions.indexApi - 获取 OSS Token 的 API 实例（this.$indexApi）
 * @param {Function} [uploadOptions.onProgress] - 上传进度回调 (uploadPercentage: number)
 * @returns {Promise<{code: number, fileUrl: string}>} 返回上传结果，包含文件访问地址
 *
 * @throws {Error} 上传失败时抛出异常
 *
 * @example
 * try {
 *   const result = await uploadFileToAliyunOSS({
 *     file: selectedFile,
 *     userId: this.currentUserId,
 *     uploadApi: this.$uploadApi,
 *     indexApi: this.$indexApi,
 *     onProgress: (percent) => {
 *       console.log(`上传进度: ${percent}%`)
 *     }
 *   })
 *   console.log("文件地址:", result.fileUrl)
 * } catch (error) {
 *   console.error("上传失败:", error.message)
 * }
 */
export async function uploadFileToAliyunOSS({
	file,
	userId,
	onProgress
}) {
	// // 防止并发上传
	// if (isFileUploading) {
	//   throw new Error("当前有文件正在上传中，请等待完成后再试")
	// }
	isFileUploading = true;

	try {
		// 第一步：获取 OSS 上传所需的签名参数
		const ossSignatureData = await requestOSSUploadSignature({
			file,
			userId
		});
		// console.log('签名参数:', ossSignatureData);

		// 第二步：使用 FormData 直传文件到 OSS
		const uploadResult = await uploadFormDataToOSS(file, ossSignatureData, onProgress);
		// console.log("上传结果:", uploadResult)

		return uploadResult;
	} finally {
		// 无论成功或失败，都要释放上传锁
		isFileUploading = false;
	}
}

/**
 * 清除 OSS 缓存（强制下次上传重新获取 Token 和签名）
 */
export function clearSignatureCache() {
	cachedTokenData = null;
	tokenExpireTimestamp = 0;
	pendingTokenPromise = null;
	cachedCommonParams = null;
	cachedUploadDir = null;
	signatureExpireTimestamp = 0;
	pendingSignaturePromise = null;
}
